import { Gender } from '@sysarb/sysarb-types/general/enums';
import type { CheckboxFilterKey, DateFilterKey, RangeFilterKey, TreeFilterKey, CountAndHighlightCheckboxFilterKey, CountAndHighlightCheckboxFilterPropertyKey } from '@/views/payEquityAudit/analyzeV2/types';
import type { OrganizationLevelCheckboxFilterKey } from '@/views/payEquityAudit/analyzeV2/types';
import type { AnalysisType } from '@/views/payEquityAudit/analyzeV2/types';

export function isGender(value: unknown): value is Gender {
    return typeof value === 'number' && Object.values(Gender).includes(value);
}

const CHECKBOX_FILTER_KEYS = new Set<string>([
    'localCareerBand', 'gender', 'locality', 'formOfEmployment', 'positionLevel', 'position',
    'statisticalcode', 'salaryYear', 'additionalDataOne', 'additionalDataSecond', 'additionalDataThird',
    'contract', 'legalEntity', 'localJobCode', 'localJobFamily', 'localJobProfile', 'localJobProfileLevel',
    'localSubFamily', 'managerCode', 'costCenter', 'organizationLevelOne', 'organizationLevelTwo',
    'organizationLevelThree', 'organizationLevelFour', 'organizationLevelFive', 'organizationLevelSix'
] satisfies CheckboxFilterKey[]);

const RANGE_FILTER_KEYS = new Set<string>([
    'standardDeviationDistance', 'performance', 'bonusTargetBonus', 'holidaysPaidSickDays',
    'bonusOneTimeBonus', 'holidaysPaidHoliday', 'equity', 'employmentTime', 'salary', 'age',
    'bonusActualBonus', 'bonusBenefitsInKind', 'companyCarAllowance', 'pensionOccupationalPension',
    'salaryExchange', 'salaryIncrement', 'salaryIncrementSecond', 'salaryIncrementThird',
    'totalSalary', 'comparatio', 'overtime', 'employmentRate', 'timeInCurrentPosition'
] satisfies RangeFilterKey[]);

const DATE_FILTER_KEYS = new Set<string>(['salaryDate', 'parentalLeaveStartDate'] satisfies DateFilterKey[]);

const TREE_FILTER_KEYS = new Set<string>(['organisationNode'] satisfies TreeFilterKey[]);

const COUNT_AND_HIGHLIGHT_CHECKBOX_FILTER_KEYS = new Set<string>([
    'analyzeStatus', 'adjustments', 'insights', 'explainations'
] satisfies CountAndHighlightCheckboxFilterKey[]);

const COUNT_AND_HIGHLIGHT_CHECKBOX_FILTER_PROPERTY_KEYS_SET = new Set<string>([
    'noExplaination',
    'competingLabourMarket',
    'market',
    'prevEmployment',
    'lessExperience',
    'moreExperience',
    'lessCompetence',
    'moreCompetence',
    'lessPerformance',
    'morePerformance',
    'lessResponsibility',
    'moreResponsibility',
    'other',
    'notAnalysed',
    'analysed',
    'noSalaryIncrease',
    'salaryIncrease',
    'notFrozen',
    'frozen',
    'receivedBonus',
    'differentSalaryYear',
    'lowPerformer',
    'highPerformer',
    'manager',
    'newToJob',
    'younger',
    'older',
    'beenOnParentalLeave',
    'outlierAbove',
    'outlierBelow',
    'flaggedForAnalysis'
] satisfies CountAndHighlightCheckboxFilterPropertyKey[]);

/**
 * Type guard to check if a key is a CheckboxFilterKey
 */
export function isCheckboxFilterKey(key: unknown): key is CheckboxFilterKey {
    return typeof key === 'string' && CHECKBOX_FILTER_KEYS.has(key);
}

/**
 * Type guard to check if a key is a RangeFilterKey
 */
export function isRangeFilterKey(key: unknown): key is RangeFilterKey {
    return typeof key === 'string' && RANGE_FILTER_KEYS.has(key);
}

/**
 * Type guard to check if a key is a DateFilterKey
 */
export function isDateFilterKey(key: unknown): key is DateFilterKey {
    return typeof key === 'string' && DATE_FILTER_KEYS.has(key);
}

/**
 * Type guard to check if a key is a TreeFilterKey
 */
export function isTreeFilterKey(key: unknown): key is TreeFilterKey {
    return typeof key === 'string' && TREE_FILTER_KEYS.has(key);
}

/**
 * Type guard to check if a key is a CountAndHighlightCheckboxFilterKey
 */
export function isCountAndHighlightCheckboxFilterKey(key: unknown): key is CountAndHighlightCheckboxFilterKey {
    return typeof key === 'string' && COUNT_AND_HIGHLIGHT_CHECKBOX_FILTER_KEYS.has(key);
}

/**
 * Check if a key is an organization level checkbox filter key
 * @param key - The key to check
 * @returns True if the key is an organization level checkbox filter key, false otherwise
 */
export function isOrganizationLevelCheckboxFilterKey(key: string): key is OrganizationLevelCheckboxFilterKey {
    return key === 'organizationLevelOne' || key === 'organizationLevelTwo' || key === 'organizationLevelThree' || key === 'organizationLevelFour' || key === 'organizationLevelFive' || key === 'organizationLevelSix';
}

/**
 * Checks if a key is a valid count and highlight checkbox filter property key.
 *
 * @param key - The key to check
 * @returns True if the key is a valid count and highlight checkbox filter property key, false otherwise
 */
export function isCountAndHighlightCheckboxFilterPropertyKey(key: unknown): key is CountAndHighlightCheckboxFilterPropertyKey {
    return typeof key === 'string' && COUNT_AND_HIGHLIGHT_CHECKBOX_FILTER_PROPERTY_KEYS_SET.has(key);
}

export function isAnalysisType(analysisType: unknown): analysisType is AnalysisType {
    if (typeof analysisType !== 'string') {
        return false;
    }

    return ['predicted-salary', 'structure', 'explore', 'explanations-and-adjustments'].includes(analysisType);
}
